/**
 * THE PALETTE, one typed object per mode (docs/STRATA.md, ruled
 * 2026-09-18: no CSS at all). Every colour the app paints is a field
 * here, in light and in dark; a component reads the palette in force
 * from the theme context (usePalette) and never holds a hex of its own.
 *
 * The values are the ones the CSS custom properties carried until this
 * ruling: zinc neutrals on white and near-black, the meaning paints one
 * ramp step darker on light so text and borders clear the ground, and
 * the dark set validated for colour-vision deficiency and contrast on
 * 2026-08-06. The light categorical five owe the same audit. Slot 3 is
 * STRUCTURAL (added 2026-09-13).
 */

export type Hex = `#${string}`;
export type Rgba = `rgba(${string})`;

export interface Palette {
  /** Grounds and lines. */
  readonly surface: Hex;
  readonly surfaceRaised: Hex;
  readonly surfaceGlass: Rgba;
  readonly hairline: Rgba;
  readonly shadow: Rgba;
  /** Text wears ink. */
  readonly ink: Hex;
  readonly inkStrong: Hex;
  readonly inkSecondary: Hex;
  readonly inkMuted: Hex;
  readonly inkFaint: Hex;
  /** The brand as TEXT: dark enough on the light surfaces (the brand teal itself is a fill, 2:1 on white). */
  readonly brandInk: Hex;
  /** Liveness of the stream. */
  readonly ok: Hex;
  readonly bad: Hex;
  /** The meaning paints (strata/paints.ts is the map onto facts). */
  readonly complying: Hex;
  readonly violating: Hex;
  readonly absence: Hex;
  /** The badge that flags a thing waiting on a person: the absence amber as a pale ground, and the deeper amber its words wear. */
  readonly absenceWash: Hex;
  readonly absenceInk: Hex;
  /** The same pair for an approved leaf (green) and for a draft or a new box (blue): a pale ground and the deeper ink its words wear. */
  readonly approvedWash: Hex;
  readonly approvedInk: Hex;
  readonly blueWash: Hex;
  readonly blueInk: Hex;
  readonly blue: Hex;
  readonly red: Hex;
  /** The words a fix strikes (Figma 2:2's before): a pale red ground and the deeper red its words wear. */
  readonly redWash: Hex;
  readonly redInk: Hex;
  /** The keyboard letter beside a verb on the filled Accept (green) and Deny (red) buttons: the pale tint of the button's own ink that clears 4.5:1 on its fill (the wash and the surface blend did not, 4.4 and 4.2 light, 4.05 dark). */
  readonly acceptHint: Hex;
  readonly denyHint: Hex;
  /** The categorical series, fixed order; consumers map domains onto slots. */
  readonly categorical: readonly [Hex, Hex, Hex, Hex, Hex];
  /** The canvas: a drawing surface of its own, never the page ground. */
  readonly canvasGround: Hex;
  readonly canvasGrid: Hex;
  readonly canvasStroke: Hex;
  readonly canvasPopup: Rgba;
  /** The instant preview's provisional building: visibly gray in every
   * mode, so the eye never mistakes a local mesh for server truth. */
  readonly canvasProvisional: Hex;
}

export const LIGHT: Palette = {
  surface: '#ffffff',
  surfaceRaised: '#f4f4f5',
  surfaceGlass: 'rgba(255,255,255,0.72)',
  hairline: 'rgba(9,9,11,0.12)',
  shadow: 'rgba(9,9,11,0.18)',
  ink: '#18181b',
  inkStrong: '#09090b',
  inkSecondary: '#52525b',
  inkMuted: '#6b6b74',
  inkFaint: '#d4d4d8',
  brandInk: '#00727d',
  ok: '#16a34a',
  bad: '#dc2626',
  complying: '#15803d',
  violating: '#dc2626',
  absence: '#b45309',
  absenceWash: '#fef3c7',
  absenceInk: '#92400e',
  approvedWash: '#d1fae5',
  approvedInk: '#065f46',
  blueWash: '#dbeafe',
  blueInk: '#1e40af',
  blue: '#2563eb',
  red: '#b91c1c',
  redWash: '#fee2e2',
  redInk: '#991b1b',
  acceptHint: '#ecfdf5',
  denyHint: '#fef2f2',
  categorical: ['#d97706', '#2563eb', '#047857', '#be185d', '#7c3aed'],
  canvasGround: '#e4e4e7',
  canvasGrid: '#a1a1aa',
  canvasStroke: '#09090b',
  canvasPopup: 'rgba(244,244,245,0.85)',
  canvasProvisional: '#9ca3af',
};

export const DARK: Palette = {
  surface: '#09090b',
  surfaceRaised: '#131316',
  surfaceGlass: 'rgba(9,9,11,0.65)',
  hairline: 'rgba(255,255,255,0.10)',
  shadow: 'rgba(0,0,0,0.50)',
  ink: '#f4f4f5',
  inkStrong: '#ffffff',
  inkSecondary: '#a1a1aa',
  inkMuted: '#d4d4d8',
  inkFaint: '#374151',
  brandInk: '#00c0cd',
  ok: '#22c55e',
  bad: '#ef4444',
  complying: '#34d399',
  violating: '#f87171',
  absence: '#fbbf24',
  absenceWash: '#422006',
  absenceInk: '#fcd34d',
  approvedWash: '#064e3b',
  approvedInk: '#6ee7b7',
  blueWash: '#172554',
  blueInk: '#93c5fd',
  blue: '#60a5fa',
  red: '#ef4444',
  redWash: '#450a0a',
  redInk: '#fca5a5',
  acceptHint: '#064e3b',
  denyHint: '#240808',
  categorical: ['#f59e0b', '#3b82f6', '#10b981', '#f472b6', '#a78bfa'],
  canvasGround: '#0f204b',
  canvasGrid: '#22314f',
  canvasStroke: '#ffffff',
  canvasPopup: 'rgba(17,24,39,0.85)',
  canvasProvisional: '#9ca3af',
};
